import { useEffect } from "react";
import LearningContentBlock from "./LearningContentBlock";
import LearningExercise from "./exercises/LearningExercise";
import { useLanguage } from "../../i18n/LanguageContext";

export default function LearningTopicPage({
  topic,
  translation,
  submittingLearningExercise,
  submitLearningExercise,
  fetchTopicProgress,
  markTopicReviewed,
  undoTopicReviewed,
  getTopicReviewStatus,
  syncLearningProgress,
  topicTranslations = [],
  selectedTopicTree = [],
  contentBlocks = [],
  topicExercises = [],
  goBack,
  styles,
}) {
  const { language, t } = useLanguage();

  const getTopicTranslation = (topicId) => {
    if (language === "en") {
      return "";
    }

    return (
      topicTranslations.find(
        (t) => t.topic_id === topicId
      )?.translation || ""
    );
  };

  const mainTopicTranslation =
    getTopicTranslation(topic.id);

  const childTopics = selectedTopicTree.filter(
    (t) => t.depth >= 0
  );

  const reviewedCount = childTopics.filter(
    (t) =>
      getTopicReviewStatus(t.id) === "reviewed"
  ).length;

  const totalParts = childTopics.length;

  const completionPct =
    totalParts > 0
      ? Math.round(
          (reviewedCount / totalParts) * 100
        )
      : 0;

  const handleMarkReviewed = async (topicId) => {
    const success =
      await markTopicReviewed(topicId);

    if (!success) return;

    const childTopicIds =
      childTopics.map((t) => t.id);

    await syncLearningProgress({
      mainTopicId: topic.id,
      childTopicIds,
    });
  };

  const handleUndoReviewed = async (topicId) => {
    const success =
      await undoTopicReviewed(topicId);

    if (!success) return;

    const childTopicIds =
      childTopics.map((t) => t.id);

    await syncLearningProgress({
      mainTopicId: topic.id,
      childTopicIds,
    });
  };

  useEffect(() => {
    const childTopicIds =
      selectedTopicTree
        .filter((t) => t.depth >= 0)
        .map((t) => t.id);

    if (childTopicIds.length > 0) {
      fetchTopicProgress(childTopicIds);
    }
  }, [
    selectedTopicTree,
    fetchTopicProgress,
  ]);

  return (
    <div style={styles.card}>
      <div style={styles.topBar}>
        <div>
          <div style={styles.quizPath}>
            {t("learningTopic.topic")}{" "}
            {topic.topic_number}
          </div>

          <h2 style={styles.sectionTitle}>
            {topic.topic_name_en}
          </h2>

          {mainTopicTranslation && (
            <div
              style={{
                ...styles.quizPath,
                marginTop: "4px",
                fontSize: "18px",
              }}
            >
              {mainTopicTranslation}
            </div>
          )}
        </div>

        <button
          style={styles.secondaryButton}
          onClick={goBack}
        >
          {t("learningTopic.back")}
        </button>
      </div>

      {/* Topic Progress */}
      {totalParts > 0 && (
        <div
          style={{
            marginTop: "16px",
            marginBottom: "24px",
          }}
        >
          <div style={{ fontWeight: "700" }}>
            {t(
              "learningTopic.partsReviewed",
              {
                reviewed: reviewedCount,
                total: totalParts,
              }
            )}
          </div>

          <div
            style={{
              height: "8px",
              backgroundColor: "#e5e7eb",
              borderRadius: "999px",
              marginTop: "8px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${completionPct}%`,
                height: "100%",
                backgroundColor: "#2563eb",
                transition: "width 0.2s ease",
              }}
            />
          </div>

          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              fontSize: "14px",
            }}
          >
            {completionPct}%{" "}
            {t("learningTopic.complete")}
          </div>
        </div>
      )}

      {/* Learning Content */}
      <div style={{ marginTop: "20px" }}>
        <h3>
          {t("learningTopic.learningContent")}
        </h3>

        {selectedTopicTree.length === 0 ? (
          <div style={styles.emptyBox}>
            {t(
              "learningTopic.noContent"
            )}
          </div>
        ) : (
          selectedTopicTree.map(
            (topicNode) => {
              const blocksForTopic =
                contentBlocks
                  .filter(
                    (block) =>
                      block.topic_id ===
                      topicNode.id
                  )
                  .sort(
                    (a, b) =>
                      (a.display_order || 0) -
                      (b.display_order || 0)
                  );

              const childTranslation =
                getTopicTranslation(
                  topicNode.id
                );

              if (
                blocksForTopic.length === 0
              ) {
                return null;
              }

              const isRootTopic =
                topicNode.depth === -1;

              return (
                <div
                  key={topicNode.id}
                  style={{
                    marginTop: isRootTopic
                      ? "16px"
                      : "30px",
                  }}
                >
                  {!isRootTopic && (
                    <div
                      style={{
                        marginBottom: "12px",
                        paddingBottom: "8px",
                        borderBottom:
                          "1px solid #e5e7eb",
                      }}
                    >
                      <h3
                        style={{
                          marginTop: 0,
                          marginBottom: "4px",
                        }}
                      >
                        {
                          topicNode.topic_name_en
                        }
                      </h3>

                      {childTranslation && (
                        <div
                          style={{
                            color: "#6b7280",
                          }}
                        >
                          {childTranslation}
                        </div>
                      )}
                    </div>
                  )}

                  {blocksForTopic.map(
                    (block) => (
                      <LearningContentBlock
                        key={block.id}
                        block={block}
                      />
                    )
                  )}

                  {!isRootTopic && (
                    <div
                      style={{
                        marginTop: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {getTopicReviewStatus(
                        topicNode.id
                      ) === "reviewed" ? (
                        <>
                          <div
                            style={{
                              fontWeight: "700",
                              color: "#16a34a",
                            }}
                          >
                            ✓{" "}
                            {t(
                              "learningTopic.reviewed"
                            )}
                          </div>

                          <button
                            style={
                              styles.smallSecondaryButton
                            }
                            onClick={() =>
                              handleUndoReviewed(
                                topicNode.id
                              )
                            }
                          >
                            {t(
                              "learningTopic.undo"
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          style={
                            styles.smallPrimaryButton
                          }
                          onClick={() =>
                            handleMarkReviewed(
                              topicNode.id
                            )
                          }
                        >
                          {t(
                            "learningTopic.markReviewed"
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            }
          )
        )}
      </div>

      {/* Practice */}
      <div style={{ marginTop: "30px" }}>
        <h3>
          {t("learningTopic.practice")}
        </h3>

        {topicExercises.length === 0 ? (
          <div style={styles.emptyBox}>
            {t(
              "learningTopic.noExercises"
            )}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            {topicExercises.map(
              (exercise) => (
                <LearningExercise
                  key={exercise.id}
                  exercise={exercise}
                  submittingLearningExercise={
                    submittingLearningExercise
                  }
                  submitLearningExercise={
                    submitLearningExercise
                  }
                  styles={styles}
                />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}